import { useState } from "react";
import {
  Box,
  Button,
  FormField,
  FormInputGroup,
  Icon,
  ModalDialog,
  ModalDialogBody,
  ModalDialogButtons,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogNav,
  ModalDialogTitle,
  Span,
  Spinner,
  Typography,
} from "@vp/swan";
import {
  ManagedWranglerSelectionsProvider,
  useManagedWranglerSelectionsContext,
  useWranglerDataContext,
} from "@vp/wrangler-data-source";
import useBffData from "../../../../../hooks/queries/useBffData";
import { useAppState } from "../../../../root/contexts/AppContext";
import { useTranslation } from "react-i18next";
import {
  extractProductOptionsInfo,
  getLocalizedAttribute,
  getLocalizedOption,
} from "./extractProductOptionsInfo";
import { ProductOptionsSection } from "@vp/product-options-ui";
import { getTenantConfig } from "../../../../../utils/tenantHelpers";
import { COLOR_KEY } from "../../../context/GroupsAndTeamsContext";
import { usePricingContext } from "../../../../root/contexts/PricingContext";

export type ColorOptionButtonProps = {
  initialValue: string;
  onColorChange: (value: string, key: string, isValid: boolean) => void;
};

export const ColorOptionButton: React.FC<ColorOptionButtonProps> = ({
  initialValue,
  onColorChange,
}) => {
  const { culture } = useAppState();
  const { pricingContextModule } = usePricingContext();
  const { data: bffData } = useBffData();
  const { productKey, productVersion, lowestPriceAttributes } = bffData!;
  const { data: wranglerData } = useWranglerDataContext(); // use page level wrangler data for button text
  const colorOptionName = bffData!.colorOptionName!;
  const { [colorOptionName]: originalColor, ...initialSelections } =
    lowestPriceAttributes;
  const selectedColor = initialValue || originalColor;
  const localizedColorAttr = getLocalizedAttribute(
    wranglerData,
    colorOptionName,
  );
  const localizedColorValue = getLocalizedOption(
    wranglerData,
    colorOptionName,
    selectedColor,
  );

  const [isOpen, setIsOpen] = useState(false);

  return (
    <FormField
      className="teams-detail-color-option-field"
      marginRight={{ sm: "between-actions" }}
    >
      <FormInputGroup>
        <input type="hidden" name="color" value={localizedColorValue} />
        <Button
          className="teams-detail-color-option-button"
          onClick={() => setIsOpen(true)}
        >
          <Span className="teams-detail-color-option-name" overflow="hidden">
            {`${localizedColorAttr}: `}
            <Span fontSkin="body-standard">{localizedColorValue}</Span>
          </Span>
          <Icon iconType="chevronRight" />
        </Button>
      </FormInputGroup>
      <ModalDialog
        isOpen={isOpen}
        variant="panel-right"
        bodyWidth="capped"
        onRequestDismiss={() => setIsOpen(false)}
        onlyRenderWhenOpen
      >
        <ManagedWranglerSelectionsProvider
          merchant="Vistaprint"
          culture={culture}
          productKey={productKey}
          productVersion={productVersion}
          includeVat={pricingContextModule.vatInclusive}
          priceStyle="relative"
          selectedAttributes={{
            ...lowestPriceAttributes,
            [colorOptionName]: selectedColor,
          }}
          initialSelectedAttributes={initialSelections}
          excludeInitialSelectedAttributes={true}
          merchandisingExperience="Product Page"
          requestor={getTenantConfig().requestor}
        >
          <ColorOptionModal
            onColorChange={onColorChange}
            onClose={() => setIsOpen(false)}
          />
        </ManagedWranglerSelectionsProvider>
      </ModalDialog>
    </FormField>
  );
};

type ColorOptionModalProps = {
  onColorChange: (value: string, key: string, isValid: boolean) => void;
  onClose: () => void;
};

const ColorOptionModal: React.FC<ColorOptionModalProps> = ({
  onColorChange,
  onClose,
}) => {
  const { t } = useTranslation("translation");
  const { culture } = useAppState();
  const { data: wranglerData } = useWranglerDataContext(); // use entry level wrangler data for dynamic product options UI
  const { data: bffData } = useBffData();
  const colorOptionName = bffData!.colorOptionName!;
  const { productName, lowestPriceAttributes } = bffData!;
  const { setSelections } = useManagedWranglerSelectionsContext();
  const { productOptions, currency, fractionDigits } =
    extractProductOptionsInfo(wranglerData);

  const selectedColor = wranglerData?.attributes
    .find((attr) => attr.key === colorOptionName)
    ?.options.find((opt) => opt.isSelected)?.key;

  const onConfirm = () => {
    if (!selectedColor) {
      console.error("No color selected"); // better to display a message to the user, but this case is highly unlikely
      return;
    } else {
      onColorChange(selectedColor, COLOR_KEY, true);
      onClose();
    }
  };

  return (
    <ModalDialogContent aria-label={t("color-selection-modal.aria-label")}>
      <ModalDialogNav>
        <ModalDialogCloseButton accessibleText={t("close")} />
      </ModalDialogNav>
      <ModalDialogHeader>
        <ModalDialogTitle>{productName}</ModalDialogTitle>
      </ModalDialogHeader>
      <ModalDialogBody>
        <Typography fontSkin="body-standard">
          {t("choose-color-description")}
        </Typography>
        {!!currency && !!fractionDigits && !!productOptions ? (
          <ProductOptionsSection
            hideDescriptions={true}
            showIncompatibleChoices={false}
            skin="tight"
            tenant="vistaprint"
            onChangeSelections={(selection) =>
              setSelections({ ...lowestPriceAttributes, ...selection })
            }
            productOptions={productOptions}
            priceDisplayMode="differential-unit"
            currency={currency}
            locale={culture}
            fractionDigits={fractionDigits}
            uiDensityUpdate={false}
          />
        ) : (
          <Box align="center" paddingTop={12}>
            <Spinner accessibleText={t("loading-label")} marginX="auto" />
          </Box>
        )}
      </ModalDialogBody>
      <ModalDialogFooter pinned>
        <ModalDialogButtons>
          <Button skin="primary" width="full-width" onClick={onConfirm}>
            {t("select")}
          </Button>
        </ModalDialogButtons>
      </ModalDialogFooter>
    </ModalDialogContent>
  );
};
